// LeaderboardModal.js
import React, { useEffect, useState } from 'react';
import { useCoin } from '../CoinContext';
import { supabase } from '../supabaseClient';

export default function LeaderboardModal({ open, onClose }) {
  const { walletAddress } = useCoin();
  const [rankings, setRankings] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rankingType, setRankingType] = useState('ssaltcoin'); // 'ssaltcoin' | 'ssaltaforce'

  useEffect(() => {
    if (!open) return;

    const fetchLeaderboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const headers = {};
        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`;
        }

        const response = await supabase.functions.invoke(
          rankingType === 'ssaltcoin' ? 'leaderboard' : 'leaderboard_ssaltaforce',
          { headers }
        );

        if (response.error) {
          throw new Error(response.error.message || 'Failed to fetch leaderboard');
        }

        const result = response.data;

        if (Array.isArray(result)) {
          const top10 = result.slice(0, 10);
          setRankings(top10);

          if (walletAddress) {
            const myIndex = result.findIndex(
              (user) =>
                user.wallet_address?.toLowerCase() === walletAddress.toLowerCase()
            );
            if (myIndex !== -1) {
              setMyRank({
                rank: myIndex + 1,
                ...result[myIndex],
              });
            } else {
              setMyRank(null);
            }
          }
        } else {
          throw new Error('Invalid leaderboard data format');
        }
      } catch (err) {
        console.error('Leaderboard error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [open, walletAddress, rankingType]);

  if (!open) return null;

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}.`;
  };

  const formatPoints = (points) => {
    return points?.toLocaleString() || '0';
  };

  const formatForces = (total_ssaltaforce) => {
    return total_ssaltaforce?.toLocaleString() || '0';
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '90%',
          maxWidth: 420,
          maxHeight: '85vh',
          background: 'linear-gradient(180deg, #4FC3D4 0%, #3AA8B8 100%)',
          borderRadius: 24,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          position: 'relative',
          padding: '32px 20px 20px',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Background decoration pattern */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%)
            `,
            pointerEvents: 'none',
          }}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'rgba(255,255,255,0.3)',
            fontSize: 20,
            position: 'absolute',
            top: 16,
            right: 16,
            cursor: 'pointer',
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 'bold',
            zIndex: 10,
          }}
        >
          ✕
        </button>

        {/* Header */}
        <h2
          style={{
            margin: '0 0 24px 0',
            textAlign: 'center',
            color: '#fff',
            fontSize: 28,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          • LEADERBOARD •
        </h2>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 14,
            marginBottom: 24,
          }}
        >
          <button
            onClick={() => setRankingType('ssaltcoin')}
            style={{
              padding: '8px 16px',
              borderRadius: 12,
              background: rankingType === 'ssaltcoin' ? '#FF8C42' : '#eff6fa',
              border: 'none',
              boxShadow:
                rankingType === 'ssaltcoin'
                  ? '0 2px 8px rgba(0,0,0,0.13)'
                  : undefined,
              cursor: rankingType === 'ssaltcoin' ? 'default' : 'pointer',
              outline: 'none',
            }}
            disabled={rankingType === 'ssaltcoin'}
          >
            PetCoin
          </button>
          <button
            onClick={() => setRankingType('ssaltaforce')}
            style={{
              padding: '8px 16px',
              borderRadius: 12,
              background:
                rankingType === 'ssaltaforce' ? '#FF8C42' : '#eff6fa',
              border: 'none',
              boxShadow:
                rankingType === 'ssaltaforce'
                  ? '0 2px 8px rgba(0,0,0,0.13)'
                  : undefined,
              cursor: rankingType === 'ssaltaforce' ? 'default' : 'pointer',
              outline: 'none',
            }}
            disabled={rankingType === 'ssaltaforce'}
          >
            PetForce
          </button>
        </div>

        {loading ? (
          <div
            style={{ textAlign: 'center', color: '#fff', padding: '40px 0' }}
          >
            Loading...
          </div>
        ) : error ? (
          <div
            style={{ textAlign: 'center', color: '#fff', padding: '40px 0' }}
          >
            Error: {error}
          </div>
        ) : (
          <>
            {/* My rank (if exists) */}
            {myRank && (
              <div
                style={{
                  background: 'rgba(255,255,255,0.95)',
                  borderRadius: 16,
                  padding: '16px',
                  marginBottom: 20,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background:
                      'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  {myRank.profile ? (
                    <img
                      src={myRank.profile}
                      alt={myRank.nickname || 'My profile'}
                      style={{
                        width: '150%',
                        height: '150%',
        objectFit: 'contain',
        objectPosition: 'center center', // 중앙 정렬
        transform: 'translateY(-4px)',    // 살짝 아래로 내려서 캐릭터 중심 맞추기
                      }}
                    />
                  ) : (
                    '👤'
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#666',
                      marginBottom: 2,
                      textTransform: 'uppercase',
                      fontWeight: 600,
                    }}
                  >
                    MY RANK
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: '#333',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {myRank.nickname || 'You'}
                  </div>
                </div>
                <div
                  style={{
                    background: '#FF8C42',
                    borderRadius: 12,
                    padding: '8px 16px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 16,
                    textAlign: 'center',
                    minWidth: 80,
                  }}
                >
                  <div style={{ fontSize: 11, marginBottom: 2 }}>
                    #{myRank.rank}
                  </div>
                  <div>
                    {rankingType === 'ssaltaforce'
                      ? formatForces(myRank.total_ssaltaforce)
                      : formatPoints(myRank.total_points)}
                  </div>
                </div>
              </div>
            )}

            {/* Top 10 list */}
            <div
              style={{
                maxHeight: myRank ? '400px' : '500px',
                overflowY: 'auto',
                paddingRight: 4,
              }}
            >
              {rankings.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    color: '#fff',
                    padding: '40px 0',
                  }}
                >
                  No rankings available
                </div>
              ) : (
                rankings.map((user, idx) => {
                  const rank = idx + 1;
                  const isTop3 = rank <= 3;
                  const isMyRank =
                    walletAddress &&
                    user.wallet_address?.toLowerCase() ===
                      walletAddress.toLowerCase();

                  return (
                    <div
                      key={user.id || idx}
                      style={{
                        background: isMyRank
                          ? 'rgba(255,255,255,0.95)'
                          : 'rgba(255,255,255,0.9)',
                        borderRadius: 12,
                        padding: '14px 16px',
                        marginBottom: 10,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        boxShadow: isMyRank
                          ? '0 4px 12px rgba(0,0,0,0.2)'
                          : '0 2px 8px rgba(0,0,0,0.1)',
                        border: isMyRank ? '2px solid #FFD700' : 'none',
                      }}
                    >
                      {/* Rank display */}
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: isTop3
                            ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                            : '#e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: isTop3 ? 20 : 14,
                          fontWeight: 700,
                          color: isTop3 ? '#fff' : '#666',
                          flexShrink: 0,
                        }}
                      >
                        {getRankIcon(rank)}
                      </div>

                      {/* Avatar */}
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background:
                            'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 20,
                          flexShrink: 0,
                          overflow: 'hidden',
                        }}
                      >
                        {user.profile ? (
                          <img
                            src={user.profile}
                            alt={user.nickname || 'profile'}
                            style={{
                              width: '150%',
                              height: '150%',
        objectFit: 'contain',
        objectPosition: 'center center', // 중앙 정렬
        transform: 'translateY(-4px)',    // 살짝 아래로 내려서 캐릭터 중심 맞추기
                            }}
                          />
                        ) : (
                          '👤'
                        )}
                      </div>

                      {/* Nickname */}
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 15,
                          fontWeight: 600,
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {user.nickname || `Player ${idx + 1}`}
                      </div>

                      <div
                        style={{
                          background: '#FF8C42',
                          borderRadius: 8,
                          padding: '6px 14px',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: 15,
                          minWidth: 70,
                          textAlign: 'center',
                        }}
                      >
                        {rankingType === 'ssaltaforce'
                          ? formatForces(user.total_ssaltaforce)
                          : formatPoints(user.total_points)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
